import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { Quill } from 'libraries/quill';
import { withEffect } from 'mixins/withEffect';
import { EffectsContainer } from 'mixins/withEffects';
import { TopicThread } from 'models/topicThread';
import { ServicesContext } from 'services/context';
import { ChatEditorVMConfig, IChatEditorVM } from '../chat/chat.interfaces';
import './threadTranslation.html';
import './threadTranslation.scss';
import ThreadTranslationEditorVMValidator from './validator';

const Keyboard = Quill.import('modules/keyboard');

components.preload('chat-editor');

export interface ThreadTranslationEditorVMConfig {
    thread: SubscribableOrNullableValue<TopicThread>

    save?: () => void
    cancel?: () => void
}

@injectable()
export default class ThreadTranslationEditorVM {
    private thread: Subscribable<TopicThread | undefined>
    private validator = new ThreadTranslationEditorVMValidator(this)

    private saveHandler: () => void
    private cancelHandler: () => void

    labels = labels

    contentEditorConfig: ChatEditorVMConfig
    contentEditor = ko.observable<IChatEditorVM>();

    content: Subscribable<string>

    isSaving = ko.observable(false)
    areErrorsShown = ko.observable(false)

    constructor(
        @inject(wccModules.componentConfig) config: ThreadTranslationEditorVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.servicesContext) private ctx: ServicesContext,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const thread = ko.flattenComputed(config.thread);

        this.thread = thread;

        this.saveHandler = config.save ?? _.noop;
        this.cancelHandler = config.cancel ?? _.noop;

        this.contentEditorConfig = {
            background: false,
            noBorder: true,

            formats: ['wcc-emoji'],

            bindings: {
                //'handleShiftEnter': { key: Keyboard.keys.ENTER, shiftKey: true, handler: () => this.save() },
                'tab': { key: Keyboard.keys.TAB, handler: () => true }
            }
        }

        this.content = this.contentEditor.pluck(editor => editor.content, '').extend({ validate: () => this.validator.validateContent() });

        const isReady = ko.pureComputed(() => this.contentEditor() != undefined && thread() != undefined);

        effects.register(editor => {
            if (editor != undefined) {
                return withEffect(isReady => {
                    if (isReady) {
                        return withEffect(thread => {
                            editor?.setContent(thread?.translatedContent() ?? '');
                        }, [thread])
                    }
                }, [editor.isReady]);
            }
        }, [this.contentEditor]);

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'thread-translation-editor': true
            }
        });
    }

    save = async () => {
        this.areErrorsShown(false);
        this.areErrorsShown(true);

        if (!this.validate() || this.isSaving()) return;

        try {
            this.isSaving(true);

            const thread = this.thread();

            if (thread == undefined)
                throw new Error(messages.InvalidRequest);

            const jsonThread = thread.toJson();
            jsonThread.TranslatedThreadContent = this.content();

            await this.ctx.commentsService.saveTranslation(jsonThread);
            this.saveHandler();
        } catch (ex) {
            system.handleError(ex);
        } finally {
            this.isSaving(false);
        }
    }

    cancel = () => {
        const originalTranslation = this.thread()?.translatedContent() ?? '';

        if (originalTranslation !== this.content())
            system.confirm(messages.AreYouSureYouWantToCancelAllYourChanges, this.cancelHandler);
        else
            this.cancelHandler();
    }

    private validate() {
        const error = this.validator.validate();

        if (error != undefined)
            system.handleError(error);

        return error == undefined;
    }
}